import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar, ArrowLeft, MapPin, Clock, AlertCircle, AlertTriangle, Plus, Check, X, Loader2, MessageSquare, Timer, Copy, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { toast } from 'sonner';
import { getSlotLabel, getSlotHelperText, getSlotStyles } from '../utils/slotLabels';
import CreditPaywall from '../components/CreditPaywall';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to calculate time remaining
const getTimeRemaining = (untilDate) => {
  if (!untilDate) return null;
  const now = new Date();
  const until = new Date(untilDate);
  const diffMs = until - now;
  
  if (diffMs <= 0) return { expired: true, text: 'Expired' };
  
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return { expired: false, urgent: false, text: `${days}d ${hours % 24}h remaining` };
  }
  if (hours > 0) {
    return { expired: false, urgent: hours < 6, text: `${hours}h ${minutes}m remaining` };
  }
  return { expired: false, urgent: true, text: `${minutes}m remaining` };
};

const HoldSlotCard = ({ slot, hold, onUpdate, onCheckOverlaps, overlaps, isOwner, eventId }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    day: hold?.day || '',
    time: hold?.time || '',
    until: hold?.until || '',
    status: hold?.status || 'Empty'
  });
  const [saving, setSaving] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();

  const isEmpty = !hold || hold.status === 'Empty';
  const isActive = hold?.status === 'Active';
  const isReleased = hold?.status === 'Released';
  const isExpired = hold?.status === 'Expired';

  // Update time remaining every minute
  useEffect(() => {
    if (hold?.until && isActive) {
      setTimeRemaining(getTimeRemaining(hold.until));
      const interval = setInterval(() => {
        setTimeRemaining(getTimeRemaining(hold.until));
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [hold?.until, isActive]);

  const handleSave = async () => {
    if (!formData.day) {
      toast.error('Please select a date');
      return;
    }
    setSaving(true);
    try {
      await onUpdate(slot, {
        day: formData.day,
        time: formData.time,
        until: formData.until || null,
        status: 'Active'
      });
      setEditing(false);
      if (formData.day) {
        onCheckOverlaps(formData.day);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRelease = async () => {
    setSaving(true);
    try {
      await onUpdate(slot, { status: 'Released' });
    } finally {
      setSaving(false);
    }
  };

  const handleStartConversation = async (overlap) => {
    try {
      const response = await axios.post(`${API}/conversations`, {
        plannerBId: overlap.plannerId,
        eventId: eventId,
        holdDay: hold.day
      }, { headers: getAuthHeaders() });
      navigate(`/conversations/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const slotOverlaps = overlaps.filter(o => o.holdDay === hold?.day);
  const slotStyles = getSlotStyles(slot);

  return (
    <Card className={`${
      isEmpty ? 'hold-empty border-dashed' : 
      isActive ? `hold-active ${slotStyles.border}` : 
      isReleased ? 'hold-released' : 
      isExpired ? 'hold-expired' : slotStyles.border
    } ${slot === 1 && !isEmpty ? slotStyles.bg : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className={`text-base ${slotStyles.text}`}>{getSlotLabel(slot)} Hold</CardTitle>
            <p className={`text-xs ${slotStyles.helperColor} mt-0.5`}>{getSlotHelperText(slot)}</p>
          </div>
          <div className="flex items-center gap-2">
            {timeRemaining && isActive && (
              <Badge 
                variant="outline" 
                className={`text-xs ${
                  timeRemaining.urgent 
                    ? 'bg-rose-50 text-rose-700 border-rose-200' 
                    : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}
              >
                <Timer className="h-3 w-3 mr-1" />
                {timeRemaining.text}
              </Badge>
            )}
            <Badge 
              variant={isEmpty ? 'outline' : isActive ? 'default' : 'secondary'} 
              className={
                isActive ? 'bg-emerald-600' : 
                isExpired ? 'bg-rose-100 text-rose-700' : ''
              }
            >
              {isEmpty ? 'Empty' : hold.status}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={formData.day}
                onChange={(e) => setFormData({ ...formData, day: e.target.value })}
                data-testid={`hold-${slot}-date-input`}
              />
            </div>
            <div className="space-y-2">
              <Label>Time (e.g., 9 AM - 5 PM)</Label>
              <Input
                placeholder="9 AM - 5 PM"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                data-testid={`hold-${slot}-time-input`}
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Timer className="h-4 w-4 text-muted-foreground" />
                Hold Until (optional)
              </Label>
              <Input
                type="datetime-local"
                value={formData.until}
                onChange={(e) => setFormData({ ...formData, until: e.target.value })}
                data-testid={`hold-${slot}-until-input`}
              />
              <p className="text-xs text-muted-foreground">
                Hold will automatically expire after this time
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid={`hold-${slot}-save-btn`}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            </div>
          </div>
        ) : isEmpty ? (
          <div className="text-center py-4">
            {isOwner && (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid={`hold-${slot}-add-btn`}>
                <Plus className="h-4 w-4 mr-1" />
                Add Hold
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4" />
              <span className="font-medium">{hold.day}</span>
            </div>
            {hold.time && (
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4" />
                <span>{hold.time}</span>
              </div>
            )}
            {hold.until && (
              <div className={`flex items-center gap-2 text-xs p-2 rounded ${
                timeRemaining?.urgent 
                  ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' 
                  : timeRemaining?.expired 
                    ? 'bg-muted text-muted-foreground'
                    : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
              }`}>
                <Timer className="h-3 w-3" />
                <span>
                  {timeRemaining?.expired 
                    ? 'Hold deadline passed' 
                    : `Expires: ${new Date(hold.until).toLocaleString()}`
                  }
                </span>
              </div>
            )}
            
            {/* Urgent expiration warning */}
            {timeRemaining?.urgent && !timeRemaining?.expired && isActive && (
              <div className="p-2 bg-rose-100 border border-rose-200 rounded-lg text-xs text-rose-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <span>This hold is expiring soon! Consider extending or publishing.</span>
              </div>
            )}
            
            {/* Overlaps */}
            {isActive && slotOverlaps.length > 0 && (
              <div className="mt-4 space-y-3">
                {/* Same city overlaps */}
                {slotOverlaps.filter(o => !o.isNearby).length > 0 && (
                  <div className="p-3 rounded-lg overlap-warning border">
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <AlertCircle className="h-4 w-4" />
                      Same City Overlaps ({slotOverlaps.filter(o => !o.isNearby).length})
                    </div>
                    <div className="space-y-2">
                      {slotOverlaps.filter(o => !o.isNearby).map((overlap, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-card/50 p-2 rounded">
                          <div className="flex items-center gap-2">
                            <img 
                              src={overlap.plannerPhoto} 
                              alt={overlap.plannerName}
                              className="w-6 h-6 rounded-full"
                            />
                            <div className="text-xs">
                              <p className="font-medium">{overlap.plannerName}</p>
                              <p className="text-muted-foreground">{overlap.eventTitle}</p>
                              {(overlap.city || overlap.state) && (
                                <p className="text-muted-foreground">{overlap.city}{overlap.city && overlap.state ? ', ' : ''}{overlap.state}</p>
                              )}
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 text-xs"
                            onClick={() => handleStartConversation(overlap)}
                            data-testid={`coordinate-btn-${overlap.eventId}`}
                          >
                            <MessageSquare className="h-3 w-3 mr-1" />
                            Coordinate
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Nearby city overlaps */}
                {slotOverlaps.filter(o => o.isNearby).length > 0 && (
                  <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2 text-sm font-medium mb-2 text-blue-700 dark:text-blue-300">
                      <MapPin className="h-4 w-4" />
                      Nearby Events ({slotOverlaps.filter(o => o.isNearby).length})
                      {slotOverlaps[0]?.region && (
                        <span className="text-xs font-normal text-blue-500 dark:text-blue-400">• {slotOverlaps[0].region} region</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      {slotOverlaps.filter(o => o.isNearby).map((overlap, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-card/70 p-2 rounded">
                          <div className="flex items-center gap-2">
                            <img 
                              src={overlap.plannerPhoto} 
                              alt={overlap.plannerName}
                              className="w-6 h-6 rounded-full"
                            />
                            <div className="text-xs">
                              <p className="font-medium">{overlap.plannerName}</p>
                              <p className="text-muted-foreground">{overlap.eventTitle}</p>
                              {(overlap.city || overlap.state) && (
                                <p className="text-blue-600">{overlap.city}{overlap.city && overlap.state ? ', ' : ''}{overlap.state}</p>
                              )}
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                            onClick={() => handleStartConversation(overlap)}
                            data-testid={`coordinate-nearby-btn-${overlap.eventId}`}
                          >
                            <MessageSquare className="h-3 w-3 mr-1" />
                            Reach Out
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {isOwner && isActive && (
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => {
                  setFormData({
                    day: hold.day || '',
                    time: hold.time || '',
                    until: hold.until || '',
                    status: hold.status
                  });
                  setEditing(true);
                }} data-testid={`hold-${slot}-edit-btn`}>
                  Edit
                </Button>
                <Button size="sm" variant="outline" onClick={handleRelease} disabled={saving} data-testid={`hold-${slot}-release-btn`}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Release
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="ml-auto"
                  onClick={() => navigate(`/get-it-done?action=createList&eventId=${eventId}&holdSlot=${slot}`)}
                  data-testid={`hold-${slot}-todo-list-btn`}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  To-Do-List
                </Button>
              </div>
            )}
            
            {isExpired && isOwner && (
              <div className="mt-3">
                <Button size="sm" variant="outline" onClick={() => {
                  setFormData({
                    day: hold.day || '',
                    time: hold.time || '',
                    until: '',
                    status: 'Empty'
                  });
                  setEditing(true);
                }} data-testid={`hold-${slot}-renew-btn`}>
                  <Plus className="h-4 w-4 mr-1" />
                  Renew Hold
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders, refreshCreditBalance } = useAuth();
  const [event, setEvent] = useState(null);
  const [overlaps, setOverlaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallData, setPaywallData] = useState(null);
  
  // Discovery panel state
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [discoveryEvents, setDiscoveryEvents] = useState([]);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);

  const fetchEvent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events/${id}`, { headers: getAuthHeaders() });
      setEvent(response.data);
      
      // Check overlaps for all active holds (filter by city/state)
      const activeDays = [];
      for (let i = 1; i <= 3; i++) {
        const hold = response.data[`hold${i}`];
        if (hold?.status === 'Active' && hold?.day) {
          activeDays.push(hold.day);
        }
      }
      
      // Fetch overlaps for each active day, filtered by location
      const allOverlaps = [];
      const eventCity = response.data.city;
      const eventState = response.data.state;
      
      for (const day of [...new Set(activeDays)]) {
        try {
          let overlapUrl = `${API}/overlaps/${day}`;
          const params = new URLSearchParams();
          if (eventCity) params.append('city', eventCity);
          if (eventState) params.append('state', eventState);
          if (params.toString()) overlapUrl += `?${params.toString()}`;
          
          const overlapRes = await axios.get(overlapUrl, { headers: getAuthHeaders() });
          allOverlaps.push(...overlapRes.data.map(o => ({ ...o, holdDay: day })));
        } catch (e) {
          console.error('Error fetching overlaps for', day);
        }
      }
      setOverlaps(allOverlaps);
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/hub');
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeaders, navigate]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  const handleUpdateHold = async (slot, data) => {
    try {
      await axios.patch(`${API}/events/${id}/holds/${slot}`, data, { headers: getAuthHeaders() });
      toast.success('Hold updated');
      fetchEvent();
    } catch (error) {
      console.error('Hold update error:', error.response?.data);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to update hold');
      }
    }
  };

  const fetchDiscoveryEvents = async () => {
    if (!event?.city) return;
    setDiscoveryLoading(true);
    try {
      const params = new URLSearchParams({ city: event.city, limit: '10' });
      if (event.state) params.set('state', event.state);
      const res = await axios.get(`${API}/discover/events?${params}`);
      setDiscoveryEvents(res.data || []);
    } catch {
      setDiscoveryEvents([]);
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const handleDeepSearch = async () => {
    if (!event?.city) return;
    setDiscoveryLoading(true);
    try {
      const res = await axios.post(`${API}/discover/deep-search`, {
        city: event.city,
        state: event.state || '',
        lgbtq_only: true,
        time_range_months: 3,
      }, { headers: getAuthHeaders() });
      toast.success(`Deep Search found ${res.data.total_found} events. ${res.data.credits_used} credits used.`);
      await fetchDiscoveryEvents();
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Not enough credits for Deep Search (3 credits required)');
      } else {
        toast.error('Deep Search failed');
      }
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const toggleDiscovery = () => {
    if (!showDiscovery && discoveryEvents.length === 0) {
      fetchDiscoveryEvents();
    }
    setShowDiscovery(!showDiscovery);
  };

  const checkOverlaps = async (day) => {
    try {
      let overlapUrl = `${API}/overlaps/${day}`;
      const params = new URLSearchParams();
      if (event?.city) params.append('city', event.city);
      if (event?.state) params.append('state', event.state);
      if (params.toString()) overlapUrl += `?${params.toString()}`;
      
      const response = await axios.get(overlapUrl, { headers: getAuthHeaders() });
      const newOverlaps = response.data.map(o => ({ ...o, holdDay: day }));
      setOverlaps(prev => {
        const filtered = prev.filter(o => o.holdDay !== day);
        return [...filtered, ...newOverlaps];
      });
      if (newOverlaps.length > 0) {
        toast.warning(`${newOverlaps.length} overlapping hold(s) found on ${day} in ${event?.city || 'this location'}`);
      }
    } catch (error) {
      console.error('Error checking overlaps:', error.response?.data);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/login');
      }
    }
  };

  const handlePublish = async () => {
    if (!selectedSlot) {
      toast.error('Please select a slot to publish');
      return;
    }
    
    const hold = event[`hold${selectedSlot}`];
    if (!hold?.day) {
      toast.error('Selected slot has no date');
      return;
    }
    
    setPublishing(true);
    try {
      await axios.post(`${API}/events/${id}/publish?slot=${selectedSlot}`, {}, { headers: getAuthHeaders() });
      toast.success('Event published!');
      await refreshCreditBalance();
      setPublishDialogOpen(false);
      fetchEvent();
    } catch (error) {
      // Handle credit limit exceeded (402 Payment Required)
      if (error.response?.status === 402) {
        const detail = error.response.data?.detail || {};
        setPaywallData({
          currentCount: detail.current_count,
          freeLimit: detail.free_limit,
          slot: selectedSlot
        });
        setPublishDialogOpen(false);
        setPaywallOpen(true);
        return;
      }
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to publish event');
    } finally {
      setPublishing(false);
    }
  };

  // Handler for when user confirms to use credit to publish
  const handleUseCreditToPublish = async () => {
    if (!paywallData?.slot) return;
    
    await axios.post(
      `${API}/events/${id}/publish?slot=${paywallData.slot}&use_credit=true`, 
      {}, 
      { headers: getAuthHeaders() }
    );
    toast.success('Event published!');
    await refreshCreditBalance();
    fetchEvent();
  };

  const handleQuickRelease = async (slot) => {
    try {
      await axios.post(`${API}/events/${id}/quick-release/${slot}`, {}, { headers: getAuthHeaders() });
      toast.success('Hold released!');
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release hold');
    }
  };

  const handleDuplicateEvent = async () => {
    try {
      const response = await axios.post(`${API}/events/duplicate`, { eventId: id }, { headers: getAuthHeaders() });
      toast.success('Event duplicated!');
      navigate(`/events/${response.data.newEventId}`);
    } catch (error) {
      toast.error('Failed to duplicate event');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!event) return null;

  const isOwner = event.plannerId === user?.id;
  const isPublished = event.status === 'Published';
  const hasActiveHold = event.hold1?.status === 'Active' || event.hold2?.status === 'Active' || event.hold3?.status === 'Active';

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <Button variant="ghost" onClick={() => navigate('/hub')} className="mb-4" data-testid="back-to-hub-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Hub
          </Button>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{event.title}</h1>
                <Badge variant={isPublished ? 'default' : 'secondary'} className={isPublished ? 'bg-emerald-600' : ''}>
                  {event.status}
                </Badge>
              </div>
              {(event.city || event.state) && (
                <p className="text-muted-foreground flex items-center gap-1 mt-1">
                  <MapPin className="h-4 w-4" />
                  {event.city}{event.city && event.state ? ', ' : ''}{event.state}
                </p>
              )}
              {event.location && (
                <p className="text-sm text-muted-foreground pl-5">{event.location}</p>
              )}
              <p className="text-sm text-muted-foreground mt-2">
                Planner: {event.plannerName} (@{event.plannerUsername})
              </p>
              {(event.categoryName || (event.tags && event.tags.length > 0)) && (
                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  {event.categoryName && (
                    <Badge variant="outline" className="text-sm">
                      <span className="mr-1">{event.categoryIcon}</span>
                      {event.categoryName}
                    </Badge>
                  )}
                  {event.tags && event.tags.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {event.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isOwner && (
                <Button variant="outline" onClick={handleDuplicateEvent} data-testid="duplicate-event-btn">
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </Button>
              )}
              {isOwner && !isPublished && hasActiveHold && (
                <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full" data-testid="publish-event-btn">
                      Publish Event
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Publish Event</DialogTitle>
                      <DialogDescription>
                        Select which hold slot to use as the final event date. Other holds will be marked as expired.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      {[1, 2, 3].map(slot => {
                        const hold = event[`hold${slot}`];
                        if (!hold || hold.status !== 'Active') return null;
                        const styles = getSlotStyles(slot);
                        return (
                          <div 
                            key={slot}
                            className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                              selectedSlot === slot 
                                ? 'border-primary bg-primary/5' 
                                : `${styles.border} hover:border-primary/50 ${slot === 1 ? styles.bg : ''}`
                            }`}
                            onClick={() => setSelectedSlot(slot)}
                            data-testid={`publish-slot-${slot}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className={`${styles.text}`}>{getSlotLabel(slot)}</p>
                                <p className={`text-xs ${styles.helperColor}`}>{getSlotHelperText(slot)}</p>
                                <p className="text-sm text-muted-foreground mt-1">{hold.day} - {hold.time || 'No time set'}</p>
                              </div>
                              {selectedSlot === slot && (
                                <Check className="h-5 w-5 text-primary" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <Button 
                        className="w-full rounded-full" 
                        onClick={handlePublish}
                        disabled={!selectedSlot || publishing}
                        data-testid="confirm-publish-btn"
                      >
                        {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Confirm & Publish
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Published Info */}
      {isPublished && (
        <div className="bg-emerald-50 border-b border-emerald-200">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-emerald-100 rounded-full">
                <Check className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <p className="font-semibold text-emerald-900">Event Published</p>
                <p className="text-sm text-emerald-700">
                  {event.publishedDay} {event.publishedTime && `• ${event.publishedTime}`} ({getSlotLabel(event.publishedSlot)})
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        
        {/* Discovery Panel - Optional, collapsible */}
        {isOwner && !isPublished && (
          <div className="mb-6" data-testid="discovery-panel">
            <Button
              variant="outline"
              onClick={toggleDiscovery}
              className="w-full justify-between text-left h-auto py-3 px-4 bg-slate-50 hover:bg-slate-100 border-slate-200"
              data-testid="discovery-toggle-btn"
            >
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">
                  What's already happening in {event.city || 'this area'}?
                </span>
              </div>
              {showDiscovery ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            </Button>
            
            {showDiscovery && (
              <Card className="mt-2 border-slate-200 animate-in fade-in slide-in-from-top-1 duration-200">
                <CardContent className="p-4">
                  {discoveryLoading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : discoveryEvents.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-500 mb-3">Events found in {event.city} — check for date conflicts before placing holds:</p>
                      {discoveryEvents.map((de) => (
                        <div
                          key={de.id}
                          className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border border-slate-100"
                          data-testid={`discovery-event-${de.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{de.event_name}</p>
                            {de.venue_name && (
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> {de.venue_name}
                              </p>
                            )}
                          </div>
                          <div className="text-right shrink-0 ml-3">
                            <p className="text-xs font-medium text-slate-700">
                              {de.start_date ? new Date(de.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : 'TBD'}
                            </p>
                            {de.start_time && <p className="text-xs text-slate-500">{de.start_time}</p>}
                          </div>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2 text-xs"
                        onClick={handleDeepSearch}
                        disabled={discoveryLoading}
                        data-testid="deep-search-event-btn"
                      >
                        {discoveryLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Search className="h-3 w-3 mr-1" />}
                        Deep Search (3 credits)
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <p className="text-sm text-slate-500 mb-3">
                        No community events found yet for {event.city}.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDeepSearch}
                        disabled={discoveryLoading}
                        data-testid="deep-search-event-btn"
                      >
                        {discoveryLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Search className="h-3 w-3 mr-1" />}
                        Deep Search (3 credits)
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          Hold Options
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <HoldSlotCard 
            slot={1} 
            hold={event.hold1} 
            onUpdate={handleUpdateHold}
            onCheckOverlaps={checkOverlaps}
            overlaps={overlaps}
            isOwner={isOwner}
            eventId={event.id}
          />
          <HoldSlotCard 
            slot={2} 
            hold={event.hold2} 
            onUpdate={handleUpdateHold}
            onCheckOverlaps={checkOverlaps}
            overlaps={overlaps}
            isOwner={isOwner}
            eventId={event.id}
          />
          <HoldSlotCard 
            slot={3} 
            hold={event.hold3} 
            onUpdate={handleUpdateHold}
            onCheckOverlaps={checkOverlaps}
            overlaps={overlaps}
            isOwner={isOwner}
            eventId={event.id}
          />
        </div>

        {/* Overlap Summary */}
        {overlaps.length > 0 && (
          <Card className="mt-8 overlap-warning border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Overlap Summary
              </CardTitle>
              <CardDescription>
                Other planners have active holds on the same days as your holds
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {overlaps.map((overlap, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-card rounded-lg">
                    <div className="flex items-center gap-3">
                      <img 
                        src={overlap.plannerPhoto} 
                        alt={overlap.plannerName}
                        className="w-10 h-10 rounded-full"
                      />
                      <div>
                        <p className="font-medium">{overlap.plannerName}</p>
                        <p className="text-sm text-muted-foreground">
                          {overlap.eventTitle} • {overlap.holdDay} {overlap.holdTime && `• ${overlap.holdTime}`}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Credit Paywall Modal */}
      <CreditPaywall
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        requiredCredits={1}
        actionName="published events"
        currentCount={paywallData?.currentCount}
        freeLimit={paywallData?.freeLimit}
        onUseCredit={handleUseCreditToPublish}
      />
    </div>
  );
};

export default EventDetail;
